// src/utils/idbKV.js
let dbPromise = null;

function openDB() {
    if (typeof window === 'undefined' || !('indexedDB' in window)) {
        // SSR or extremely old browser. We'll just no-op gracefully.
        return Promise.resolve(null);
    }
    if (!dbPromise) {
        dbPromise = new Promise((resolve, reject) => {
            const req = indexedDB.open('energiberegnerDB', 1);
            req.onupgradeneeded = () => {
                const db = req.result;
                if (!db.objectStoreNames.contains('kv')) db.createObjectStore('kv');
            };
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
    }
    return dbPromise;
}

async function withStore(mode, fn) {
    const db = await openDB();
    if (!db) return null;
    return new Promise((resolve, reject) => {
        const tx = db.transaction('kv', mode);
        const store = tx.objectStore('kv');
        const res = fn(store);
        tx.oncomplete = () => resolve(res);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
    });
}

/* KV primitives -------------------------------------------------- */
export async function get(key) {
    return withStore('readonly', store =>
        new Promise((ok, err) => {
            const r = store.get(key);
            r.onsuccess = () => ok(r.result);
            r.onerror = () => err(r.error);
        })
    );
}

export async function set(key, value) {
    return withStore('readwrite', store =>
        new Promise((ok, err) => {
            const r = store.put(value, key);
            r.onsuccess = () => ok(true);
            r.onerror = () => err(r.error);
        })
    );
}

export async function del(key) {
    return withStore('readwrite', store =>
        new Promise((ok, err) => {
            const r = store.delete(key);
            r.onsuccess = () => ok(true);
            r.onerror = () => err(r.error);
        })
    );
}

/* JSON helpers --------------------------------------------------- */
export async function getJSON(key, fallback = null) {
    try {
        const raw = await get(key);
        return raw == null ? fallback : raw;
    } catch {
        return fallback;
    }
}
export async function setJSON(key, value) {
    return set(key, value);
}

/* Ask for persistent storage (less eviction) --------------------- */
export async function ensurePersistentStorage() {
    try {
        if (navigator.storage?.persist) {
            const have = await navigator.storage.persisted?.();
            if (!have) await navigator.storage.persist();
        }
    } catch { /* browser said no, life goes on */ }
}

/* One-time migration from localStorage ---------------------------- */
export async function migrateFromLocalStorage() {
    if (typeof window === 'undefined') return;

    const plan = [
        { key: 'projekter', json: true },
        { key: 'activeProject', json: false },
        { key: 'uvalueLibrary', json: true },
        { key: 'energiberegner:author', json: false },
    ];

    for (const { key, json } of plan) {
        try {
            const already = await get(key);
            if (already != null) continue; // IDB has it → skip

            const ls = localStorage.getItem(key);
            if (ls == null) continue;

            const val = json ? JSON.parse(ls) : ls;
            await set(key, val);
            // optional: keep localStorage as a belt-and-suspenders backup
            // localStorage.removeItem(key);
        } catch {
            /* ignore bad JSON or quota weirdness */
        }
    }
}

/* Convenience: set up both at app start -------------------------- */
export async function setupStorage() {
    await ensurePersistentStorage();
    await migrateFromLocalStorage();
}
